/**
 * Overview Dashboard - Enterprise Grade
 * Comprehensive quality overview with key metrics
 */

import React, { useState, useEffect } from 'react';
import {
  Shield, BarChart3, TrendingUp, TrendingDown, Target, Award,
  AlertTriangle, CheckCircle, Clock, Activity, Users, FileText,
  ArrowRight, ArrowLeft, Calendar, Eye
} from 'lucide-react';
import { qualityReports, qualityIncidents, qualityMetrics } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';

const OverviewDashboard = () => {
  const { state } = useAppContext();
  const selectedFacility = state.selectedFacility;
  
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  const loadData = () => {
    setLoading(true);
    
    // Calculate overall quality score
    const metrics = Object.values(qualityMetrics);
    const totalScore = metrics.reduce((sum, metric) => sum + metric.current, 0);
    const maxScore = metrics.reduce((sum, metric) => sum + metric.benchmark, 0);
    const overallScore = Math.round((totalScore / maxScore) * 100);
    
    // Get recent reports and incidents
    const recentReports = qualityReports.slice(0, 5);
    const recentIncidents = qualityIncidents.slice(0, 5);
    
    // Calculate trends
    const trends = {
      reports: {
        total: qualityReports.length,
        passed: qualityReports.filter(r => r.status === 'Passed').length,
        trend: '+2.1%'
      },
      incidents: {
        total: qualityIncidents.length,
        resolved: qualityIncidents.filter(i => i.status === 'Resolved').length,
        trend: '-15%'
      },
      metrics: {
        overall: overallScore,
        improving: metrics.filter(m => m.trend < 0).length,
        declining: metrics.filter(m => m.trend > 0).length
      }
    };
    
    setOverviewData({
      overallScore,
      trends,
      recentReports,
      recentIncidents,
      metrics: qualityMetrics
    });
    
    setLoading(false);
  };

  if (loading || !overviewData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8" />
              Quality Overview
            </h1>
            <p className="text-emerald-100 text-lg">Comprehensive quality management dashboard</p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold mb-2">{overviewData.overallScore}%</div>
            <div className="text-emerald-100">Overall Quality Score</div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{overviewData.overallScore}%</div>
              <div className="text-xs text-gray-500">Quality Score</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Overall Performance</h3>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-600 font-semibold">+2.1%</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">
                {overviewData.trends.metrics.improving}
              </div>
              <div className="text-xs text-gray-500">Improving</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Metrics Trends</h3>
          <div className="text-sm text-gray-600">
            {overviewData.trends.metrics.declining} declining
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{overviewData.trends.incidents.total}</div>
              <div className="text-xs text-gray-500">Total Incidents</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Incident Management</h3>
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-600 font-semibold">{overviewData.trends.incidents.trend}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{overviewData.trends.reports.total}</div>
              <div className="text-xs text-gray-500">Total Reports</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800 mb-2">Quality Reports</h3>
          <div className="text-sm text-gray-600">
            {overviewData.trends.reports.passed} passed
          </div>
        </div>
      </div>

      {/* Quick Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(overviewData.metrics).slice(0, 4).map(([key, metric]) => {
          const isImproving = metric.trend < 0;
          return (
            <div key={key} className="bg-white rounded-xl p-4 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm capitalize text-gray-700">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </h4>
                {isImproving ? (
                  <TrendingDown className="w-4 h-4 text-green-600" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-red-600" />
                )}
              </div>
              <div className="text-2xl font-bold text-gray-800 mb-1">{metric.current}</div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Target: {metric.target}</span>
                <span className={`font-semibold ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(metric.trend)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Reports */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="w-6 h-6 text-emerald-600" />
              Recent Quality Reports
            </h3>
            <button className="text-sm text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1">
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            {overviewData.recentReports.map((report) => (
              <div key={report.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">{report.title}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    report.status === 'Passed' ? 'bg-green-100 text-green-800' :
                    report.status === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {report.status}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{report.facility} • {report.date}</span>
                  <span className="font-semibold text-gray-800">{report.score}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Incidents */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              Recent Incidents
            </h3>
            <button className="text-sm text-orange-600 hover:text-orange-700 font-semibold flex items-center gap-1">
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            {overviewData.recentIncidents.map((incident) => (
              <div key={incident.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">{incident.type}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    incident.severity === 'Major' ? 'bg-red-100 text-red-800' :
                    incident.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {incident.severity}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{incident.resident} • {incident.facility}</span>
                  <span className="text-xs">{incident.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-blue-800 flex items-center gap-3">
              <Shield className="w-7 h-7 text-blue-600" />
              Compliance Summary
            </h3>
            <p className="text-blue-600 mt-1">Key compliance areas and status</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-gray-800">NQIP</h4>
            </div>
            <div className="text-2xl font-bold text-green-600 mb-1">94.2%</div>
            <div className="text-xs text-gray-600">Compliant</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h4 className="font-semibold text-gray-800">SIRS</h4>
            </div>
            <div className="text-2xl font-bold text-red-600 mb-1">98.5%</div>
            <div className="text-xs text-gray-600">Response Rate</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-gray-800">Audits</h4>
            </div>
            <div className="text-2xl font-bold text-purple-600 mb-1">96.8%</div>
            <div className="text-xs text-gray-600">Average Score</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-indigo-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              <h4 className="font-semibold text-gray-800">Analytics</h4>
            </div>
            <div className="text-2xl font-bold text-indigo-600 mb-1">92.3%</div>
            <div className="text-xs text-gray-600">Quality Metrics</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
